"use client"

import { useEffect, useState } from "react"
import { defaultTrainingRequest, type ITrainingRequest } from "../../../../shared/models/TrainingRequestModel"
import { useAppContext } from "../../../../shared/functions/Context"
import { defaultUser, type IUser } from "../../../../shared/models/User"
import { observer } from "mobx-react-lite"
import showModalFromId from "../../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { dateFormat_YY_MM_DD } from "../../../shared/utils/utils"

const MyTrainingRequests = observer(() => {
  const { store, api, ui } = useAppContext()
  const me = store.auth.me?.asJson
  const departments = store.department.all

  const allTrainings = store.trainingRequest.all
    .map((training) => training.asJson)
    .filter((training) => training.uid === me?.uid && training.requestType === "Employee")

  const [currentUser, setCurrentUser] = useState<IUser>({ ...defaultUser })
  const [courseRequest, setCourseRequest] = useState<ITrainingRequest>({
    ...defaultTrainingRequest,
  })
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const itemsPerPage = 10

  const paginatedTrainings = allTrainings.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)

  const getDepartmentName = (departmentId: string) => {
    const department = departments.find((dept) => dept.asJson.id === departmentId)
    return department ? department.asJson.name : "Unknown Department"
  }

  const handleRowClick = (trainingRequest: ITrainingRequest) => {
    store.trainingRequest.select(trainingRequest)
    showModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_VIEW_TRAINING_MODAL)
  }

  const handleAddTraining = () => {
    showModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_CREATE_TRAINING_CATALOG_MODAL)
  }

  const handleRequestTraining = () => {
    showModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_REQUEST_TRAINING_MODAL)
  }

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase() || "unknown"
    switch (statusLower) {
      case "approved":
        return "bg-[#4bb543] text-white"
      case "pending":
        return "bg-[#faa05a] text-white"
      case "rejected":
        return "bg-[#dc3545] text-white"
      default:
        return "bg-[#a9a9ab] text-white"
    }
  }

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      try {
        await api.trainingRequest.getAll()
        await api.department.getAll()
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        })
      }
      setLoading(false)
    }
    loadAll()
  }, [api.trainingRequest, api.department, ui.snackbar])

  useEffect(() => {
    if (me) {
      setCurrentUser(me)
    }
  }, [store.personalDevelopment.selected])

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0))
  }

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev < Math.ceil(allTrainings.length / itemsPerPage) - 1 ? prev + 1 : prev))
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h3 className="text-2xl font-semibold text-[#516d85] mb-2">Requested Courses</h3>
        <h4 className="text-base text-[#666]">What to learn next</h4>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 mb-6">
        <button
          onClick={handleAddTraining}
          className="px-5 py-2.5 text-sm font-medium text-white bg-[#84cbe9] rounded hover:bg-[#3f6a8e] transition-colors"
        >
          Add Training to Catalog
        </button>
        <button
          onClick={handleRequestTraining}
          className="px-5 py-2.5 text-sm font-medium text-white bg-[#e7a637] rounded hover:bg-[#d89530] transition-colors"
        >
          Request Training
        </button>
      </div>

      {paginatedTrainings.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-[#ccc] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#c5dee9]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#516d85] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#516d85] uppercase tracking-wider">
                    Course Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#516d85] uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#516d85] uppercase tracking-wider">
                    Date Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#516d85] uppercase tracking-wider">
                    Location
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e8e8e8]">
                {paginatedTrainings.map((trainingRequest, index) => (
                  <tr
                    key={index}
                    onClick={() => handleRowClick(trainingRequest)}
                    className="hover:bg-[#e4eefd] cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          trainingRequest.status,
                        )}`}
                      >
                        {trainingRequest.status || "Unknown"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-[#516d85]">{trainingRequest.courseName}</td>
                    <td className="px-6 py-4 text-sm text-[#666]">{getDepartmentName(trainingRequest.department)}</td>
                    <td className="px-6 py-4 text-sm text-[#666]">
                      {dateFormat_YY_MM_DD(trainingRequest.dateRequested)}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#666]">{trainingRequest.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-[#ccc] p-12 text-center">
          <p className="text-[#666] text-base">No training requests available 😔</p>
        </div>
      )}

      {/* Pagination Controls */}
      {Math.ceil(allTrainings.length / itemsPerPage) > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-[#84cbe9] rounded hover:bg-[#3f6a8e] disabled:bg-[#ccc] disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-[#666]">
            Page {currentPage + 1} of {Math.ceil(allTrainings.length / itemsPerPage)}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage >= Math.ceil(allTrainings.length / itemsPerPage) - 1}
            className="px-4 py-2 text-sm font-medium text-white bg-[#84cbe9] rounded hover:bg-[#3f6a8e] disabled:bg-[#ccc] disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
})

export default MyTrainingRequests
